import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useState,
  useDeferredValue,
  useCallback,
  useEffect,
} from "react";
import { useDebounce } from "../hooks/debounce";

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Types

type QueryCallback = (query: string) => void;

type SearchQueryContextType = {
  // Raw query from input
  query: string;
  setQuery: (query: string, instant?: boolean) => void;

  // Transformed queries
  deferredQuery: string;
  normalizedQuery: string;
  debouncedQuery: string;

  // Query change callback registration
  registerQueryChangeCallback: (callback: QueryCallback) => () => void;

  // Submit/enter callback registration
  registerQuerySubmitCallback: (callback: QueryCallback) => () => void;
  triggerQuerySubmit: () => void;
};

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Context Provider

export const SearchQueryContext = createContext<SearchQueryContextType | undefined>(undefined);

export function SearchQueryProvider({ children }: PropsWithChildren) {
  const [query, setQuery] = useState<string>("");
  const [shouldDebounce, setShouldDebounce] = useState<boolean>(true);
  const [queryChangeCallbacks, setQueryChangeCallbacks] = useState<Set<QueryCallback>>(new Set());
  const [querySubmitCallbacks, setQuerySubmitCallbacks] = useState<Set<QueryCallback>>(new Set());

  // Don't block the main thread
  const deferredQuery = useDeferredValue(query);

  // Normalize the query so we can use it for the search
  const normalizedQuery = deferredQuery.trim();

  // Debounce the query (when appropriate) to prevent excessive requests
  const debounceDelay = shouldDebounce ? 150 : 0;
  const [debouncedQuery] = useDebounce(normalizedQuery, debounceDelay);

  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  // Custom Setter Function

  const handleSetQuery = useCallback(
    (newQuery: string, instant = false) => {
      setShouldDebounce(!instant);
      setQuery(newQuery);

      // Call all registered callbacks
      queryChangeCallbacks.forEach((callback) => callback(newQuery));
    },
    [queryChangeCallbacks],
  );

  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  // Callback Registration System

  const registerQueryChangeCallback = useCallback((callback: QueryCallback) => {
    setQueryChangeCallbacks((prev) => new Set([...prev, callback]));

    return () => {
      setQueryChangeCallbacks((prev) => {
        const next = new Set(prev);
        next.delete(callback);
        return next;
      });
    };
  }, []);

  const registerQuerySubmitCallback = useCallback((callback: QueryCallback) => {
    setQuerySubmitCallbacks((prev) => new Set([...prev, callback]));

    return () => {
      setQuerySubmitCallbacks((prev) => {
        const next = new Set(prev);
        next.delete(callback);
        return next;
      });
    };
  }, []);

  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  // Submission Trigger

  const triggerQuerySubmit = useCallback(() => {
    querySubmitCallbacks.forEach((callback) => callback(query));
  }, [query, querySubmitCallbacks]);

  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  // Render Provider

  return (
    <SearchQueryContext.Provider
      value={{
        query,
        setQuery: handleSetQuery,
        deferredQuery,
        normalizedQuery,
        debouncedQuery,
        registerQueryChangeCallback,
        registerQuerySubmitCallback,
        triggerQuerySubmit,
      }}
    >
      {children}
    </SearchQueryContext.Provider>
  );
}

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Hook

export function useSearchQuery() {
  const context = useContext(SearchQueryContext);
  if (context === undefined) {
    throw new Error("useSearchQuery cannot be used outside of a SearchQueryProvider");
  }

  return context;
}

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Query Change Callback Hook

export function useOnQueryChange(callback: (query: string) => void) {
  const { registerQueryChangeCallback } = useSearchQuery();

  useEffect(() => {
    const cleanup = registerQueryChangeCallback(callback);
    return cleanup;
  }, [callback, registerQueryChangeCallback]);
}

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Query Submit Callback Hook

export function useOnQuerySubmit(callback: (query: string) => void) {
  const { registerQuerySubmitCallback } = useSearchQuery();

  useEffect(() => {
    const cleanup = registerQuerySubmitCallback(callback);
    return cleanup;
  }, [callback, registerQuerySubmitCallback]);
}
